"use client";

/**
 * Native appointment sheet — OUR bottom sheet, not Radix.
 *
 * ⚠️ Radix Dialog demonstrably cannot position itself in this WKWebView — six
 * fix layers (classes, @supports CSS, inline styles, detection swaps) on two
 * simulators with wiped caches all rendered the same clipped panel, while
 * every PLAIN fixed-div sheet in the app (route dispositions, More menu,
 * selection bar) renders perfectly. So this is a plain fixed div, using the
 * SAME createAppointment/updateAppointment services as desktop — the payload
 * is identical, only the chrome is ours. Desktop keeps its dialog untouched.
 */
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { X, User as UserIcon, Search } from "lucide-react";
import { useUpdateLeadStatus } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import { LeadStatus } from "@/app/_module/constants/leadStatus";
import {
  useCreateAppointment,
  useUpdateAppointment,
} from "@/app/(authenticated)/appointments/_modules/hooks/queries";
import { useGetLeads } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import { appointmentStatusOptions } from "@/app/(authenticated)/appointments/_modules/constants/form-options";
import { successToast, errorToast } from "@/app/_module/services/helper";
import { Pressable, haptic } from "@/components/mobile/ui/native";
import { SheetPortal } from "@/components/mobile/SheetPortal";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";

const DURATIONS = [15, 30, 45, 60, 90, 120];

export interface SheetAppointment {
  id?: string;
  title?: string;
  apptDate?: string;
  time?: string;
  startTime?: string | Date;
  duration?: number;
  apptNotes?: string;
  status?: string;
  leadId?: string;
  leadName?: string;
  associations?: Array<{ entityType?: string; entityId?: string; entityName?: string }>;
}

export function MobileAppointmentSheet({
  open,
  onClose,
  editing,
  preselectedLeadId,
  preselectedLeadName,
}: {
  open: boolean;
  onClose: () => void;
  editing?: SheetAppointment | null;
  preselectedLeadId?: string;
  preselectedLeadName?: string;
}) {
  const router = useRouter();
  const createAppt = useCreateAppointment();
  const updateLeadStatus = useUpdateLeadStatus();
  const updateAppt = useUpdateAppointment();

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(30);
  const [customDuration, setCustomDuration] = useState(false);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("scheduled");

  const initialLead = useMemo(() => {
    const assoc = editing?.associations?.find((a) => a.entityType === "Lead");
    const id = editing?.leadId ?? assoc?.entityId ?? preselectedLeadId;
    const name = editing?.leadName ?? assoc?.entityName ?? preselectedLeadName;
    return id ? { id, name } : null;
  }, [editing, preselectedLeadId, preselectedLeadName]);

  // The lead actually attached to this appointment — starts from the record /
  // preselection, changeable via lookup below.
  const [linkedLead, setLinkedLead] = useState<{ id?: string; name?: string } | null>(null);
  const [leadQuery, setLeadQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(leadQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [leadQuery]);
  // Same search the Leads screen runs — name/phone/city, agency-scoped.
  const { data: leadResults } = useGetLeads(
    { search: debouncedQuery, page: 1, limit: 6 } as any,
  );
  const suggestions = useMemo(() => {
    if (!debouncedQuery) return [];
    const raw: any = leadResults;
    const list = raw?.items ?? raw?.data?.items ?? [];
    return Array.isArray(list) ? list.slice(0, 6) : [];
  }, [leadResults, debouncedQuery]);

  // hydrate on open
  useEffect(() => {
    if (!open) return;
    const st = editing?.startTime ? new Date(editing.startTime) : null;
    setTitle(editing?.title ?? "");
    setDate(editing?.apptDate ?? (st ? st.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)));
    setTime(editing?.time ?? (st ? `${String(st.getHours()).padStart(2, "0")}:${String(st.getMinutes()).padStart(2, "0")}` : "09:00"));
    setDuration(editing?.duration ?? 30);
    setNotes(editing?.apptNotes ?? "");
    setStatus(editing?.status ?? "scheduled");
    setLinkedLead(initialLead);
    setLeadQuery("");
  }, [open, editing, initialLead]);

  if (!open) return null;
  const busy = createAppt.isPending || updateAppt.isPending;

  const save = async () => {
    if (!title.trim() || !date || !time) {
      errorToast("Title, date and time are required");
      return;
    }
    haptic("medium");
    // The same legacy payload desktop's dialog submits — the service converts
    // apptDate+time into startTime/endTime server-side.
    const payload: any = {
      title: title.trim(),
      apptDate: date,
      time,
      duration,
      apptNotes: notes || undefined,
      ...(linkedLead?.id
        ? { leadId: linkedLead.id, associations: [{ entityType: "Lead", entityId: linkedLead!.id, entityName: linkedLead!.name }] }
        : {}),
    };
    try {
      if (editing?.id) {
        await updateAppt.mutateAsync({ appointmentId: editing.id, data: { ...payload, status } } as any);
        successToast("Appointment updated");
      } else {
        await createAppt.mutateAsync(payload);
        successToast("Appointment booked");
        // Desktop parity: booking an appointment auto-moves the linked lead
        // to the "Appt" stage (NewAppointmentDialog does the same on create).
        if (linkedLead?.id) {
          try {
            await updateLeadStatus.mutateAsync({ leadId: linkedLead.id, status: LeadStatus.APPOINTMENT });
          } catch { /* status error already toasted by the mutation */ }
        }
      }
      onClose();
    } catch (e: any) {
      errorToast(e?.message || "Could not save the appointment");
    }
  };

  const field = "h-12 w-full rounded-[12px] border border-black/[0.08] bg-white px-3 text-[16px] text-black";
  const label = "mb-1.5 mt-4 block text-[13px] font-medium text-[#6D6D72]";

  return (
    <SheetPortal>
      <div className="absolute inset-0 z-[80] bg-black/45" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 z-[90] max-h-[calc(100dvh-env(safe-area-inset-top,0px)-40px)] overflow-y-auto rounded-t-[18px] bg-[#F7F7FA] pb-[calc(env(safe-area-inset-bottom,0px)+16px)] animate-in slide-in-from-bottom duration-250">
        {/* grabber + header */}
        <div className="m-sheet-head sticky top-0 z-10 rounded-t-[18px] bg-[#F7F7FA]/95 px-4 pb-2 pt-2 backdrop-blur">
          <div className="mx-auto mb-2 h-[5px] w-9 rounded-full bg-black/20" />
          <div className="flex items-center justify-between">
            <Pressable onClick={onClose} className="py-1 text-[16px] text-primary">Cancel</Pressable>
            <span className="text-[16px] font-semibold text-black">
              {editing?.id ? "Edit Appointment" : "New Appointment"}
            </span>
            <Pressable onClick={() => void save()} className={`py-1 text-[16px] font-semibold text-primary ${busy ? "opacity-50" : ""}`}>
              {busy ? "Saving…" : "Save"}
            </Pressable>
          </div>
        </div>

        <div className="px-4">

          <label className={label}>Title *</label>
          <input className={field} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What's this appointment for?" />

          <div className="flex gap-3">
            <div className="flex-1">
              <label className={label}>Date *</label>
              <input type="date" className={field} value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="flex-1">
              <label className={label}>Time *</label>
              <input type="time" className={field} value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>

          <label className={label}>Duration</label>
          <div className="flex flex-wrap gap-2">
            {DURATIONS.map((d) => (
              <Pressable key={d} onClick={() => { haptic(); setDuration(d); setCustomDuration(false); }}
                className={`rounded-full px-3.5 py-2 text-[14px] font-medium ring-1 ${!customDuration && duration === d ? "bg-primary text-white ring-primary" : "bg-white text-[#3C3C43] ring-black/[0.08]"}`}>
                {d < 60 ? `${d}m` : `${Math.floor(d / 60)}h${d % 60 ? ` ${d % 60}m` : ""}`}
              </Pressable>
            ))}
            <Pressable onClick={() => { haptic(); setCustomDuration(true); }}
              className={`rounded-full px-3.5 py-2 text-[14px] font-medium ring-1 ${customDuration ? "bg-primary text-white ring-primary" : "bg-white text-[#3C3C43] ring-black/[0.08]"}`}>
              Custom
            </Pressable>
          </div>
          {customDuration && (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="number" min={5} max={480} step={5}
                value={duration}
                onChange={(e) => setDuration(Math.max(5, Math.min(480, Number(e.target.value) || 0)))}
                className="h-12 w-28 rounded-[12px] border border-black/[0.08] bg-white px-3 text-[16px] text-black"
                inputMode="numeric"
              />
              <span className="text-[14px] text-[#8E8E93]">minutes (max 480)</span>
            </div>
          )}

          {editing?.id && (
            <>
              <label className={label}>Status</label>
              <div className="flex flex-wrap gap-2">
                {appointmentStatusOptions.map((st) => (
                  <Pressable key={st} onClick={() => { haptic(); setStatus(st); }}
                    className={`rounded-full px-3.5 py-2 text-[14px] font-medium ring-1 ${status === st ? "bg-primary text-white ring-primary" : "bg-white text-[#3C3C43] ring-black/[0.08]"}`}>
                    {st.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </Pressable>
                ))}
              </div>
            </>
          )}

          <label className={label}>Notes</label>
          <textarea
            className="min-h-[90px] w-full rounded-[12px] border border-black/[0.08] bg-white p-3 text-[16px] text-black"
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes about the appointment…"
          />
          {/* Linked lead — tap opens the lead, ✕ unlinks. When none is
              linked, a live lookup searches the agency's leads. */}
          {linkedLead?.id ? (
            <div className="mt-2 flex w-full items-center gap-2.5 rounded-[12px] bg-primary/[0.08] px-3 py-2.5">
              <Pressable
                onClick={() => { haptic(); onClose(); router.push(`/m/leads/${linkedLead.id}`); }}
                className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <UserIcon className="h-4 w-4" strokeWidth={2.2} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[11px] font-medium uppercase tracking-wide text-primary/70">Linked lead</span>
                  <span className="block truncate text-[15px] font-semibold text-primary">{linkedLead.name || "Open lead"}</span>
                </span>
              </Pressable>
              <Pressable aria-label="Unlink lead" onClick={() => { haptic(); setLinkedLead(null); }}
                className="flex h-7 w-7 items-center justify-center rounded-full bg-black/[0.06] text-[#8E8E93]">
                <X className="h-3.5 w-3.5" />
              </Pressable>
            </div>
          ) : (
            <div className="mt-2">
              <label className={label}>Link a lead</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8E8E93]" />
                <input
                  className="h-12 w-full rounded-[12px] border border-black/[0.08] bg-white pl-9 pr-3 text-[16px] text-black"
                  placeholder="Search name, phone, or city…"
                  value={leadQuery}
                  onChange={(e) => setLeadQuery(e.target.value)}
                />
              </div>
              {suggestions.length > 0 && (
                <div className="mt-1 overflow-hidden rounded-[12px] bg-white ring-1 ring-black/[0.06]">
                  {suggestions.map((l: any) => (
                    <Pressable
                      key={l.id}
                      onClick={() => { haptic(); setLinkedLead({ id: String(l.id), name: l.name }); setLeadQuery(""); }}
                      className="flex w-full items-center gap-2.5 border-b border-black/[0.04] px-3 py-2.5 text-left last:border-b-0 active:bg-black/[0.04]"
                    >
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#E9F0FF] text-[12px] font-semibold text-[#3B62C4]">
                        {(l.name || "?").slice(0, 1).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] text-black">{l.name || "Unknown"}</span>
                        <span className="block truncate text-[12px] text-[#8E8E93]">
                          {[l.phone ? formatPhoneNumber(l.phone) : null, l.city].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    </Pressable>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </SheetPortal>
  );
}
